import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import gsap from 'gsap';
import { apiFetch, ApiError } from '../../lib/api.js';
import { BrandPanel } from './BrandPanel.js';
import './login.css';

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function shake(target: gsap.TweenTarget) {
  if (reduceMotion()) return;
  gsap.fromTo(target, { x: 0 }, { x: -7, duration: 0.07, repeat: 5, yoyo: true, clearProps: 'x', ease: 'power1.inOut' });
}

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);

  const formWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reduceMotion()) return;
    const ctx = gsap.context(() => {
      gsap.from('[data-reveal]', { opacity: 0, y: 22, duration: 0.65, stagger: 0.075, ease: 'power3.out', delay: 0.45 });
    }, formWrapRef);
    return () => ctx.revert();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError('Enter your username and password to continue.');
      shake(formWrapRef.current);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await apiFetch<{ mfaRequired: boolean; totpEnrolled: boolean }>('/auth/login', {
        method: 'POST',
        body: { username: username.trim(), password },
      });
      setPassword('');
      if (res.mfaRequired) {
        setBusy(false);
        setOtpOpen(true);
      } else {
        await queryClient.invalidateQueries({ queryKey: ['auth'] });
        navigate('/setup-2fa');
      }
    } catch (err) {
      setBusy(false);
      setError(err instanceof ApiError ? err.message : 'sign in failed');
      shake(formWrapRef.current);
    }
  };

  const onVerified = async () => {
    setOtpOpen(false);
    setSuccess(true);
    await queryClient.invalidateQueries({ queryKey: ['auth'] });
    setTimeout(() => navigate('/'), 600);
  };

  return (
    <div className="login-shell">
      <BrandPanel />

      <section className="pane">
        <div className="form-wrap" ref={formWrapRef}>
          <div className="eyebrow" data-reveal>
            SherpaDeploy Console
          </div>
          <h2 className="form-title" data-reveal>
            Sign in
          </h2>
          <p className="form-hint" data-reveal>
            Use your operator account. All sign-ins require two-factor verification.
          </p>

          <form className="login-form" onSubmit={submit} noValidate>
            <div className="lg-field" data-reveal>
              <label htmlFor="username">Username</label>
              <div className="control">
                <input
                  id="username"
                  name="username"
                  type="text"
                  placeholder="your.username"
                  autoComplete="username"
                  autoFocus
                  className={error && !username.trim() ? 'invalid' : ''}
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setError('');
                  }}
                  required
                />
              </div>
            </div>

            <div className="lg-field" data-reveal>
              <label htmlFor="password">Password</label>
              <div className="control">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  className={error && !password ? 'invalid' : ''}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  required
                />
                <button
                  type="button"
                  className={`peek ${showPassword ? 'peek-on' : ''}`}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
            </div>

            <p className={`lg-error ${error ? 'show' : ''}`} role="alert">
              {error}
            </p>

            <button className={`lg-btn ${busy ? 'loading' : ''} ${success ? 'success' : ''}`} type="submit" disabled={busy || success} data-reveal>
              <span>{success ? 'Signed in' : busy ? 'Signing in…' : 'Sign in'}</span>
              <svg className="arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
              <span className="spinner" />
            </button>
          </form>

          <p className="form-foot" data-reveal>
            Locked out? An administrator can unlock your account or reset your password.
          </p>
        </div>
      </section>

      {otpOpen ? <OtpModal onVerified={onVerified} onClose={() => setOtpOpen(false)} /> : null}
    </div>
  );
}

function OtpModal({ onVerified, onClose }: { onVerified: () => void; onClose: () => void }) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const [backupMode, setBackupMode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [error, setError] = useState('');
  const [bad, setBad] = useState(false);
  const [busy, setBusy] = useState(false);
  const [verified, setVerified] = useState(false);

  const cardRef = useRef<HTMLDivElement>(null);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (!reduceMotion() && cardRef.current) {
      gsap.fromTo(cardRef.current, { y: 28, scale: 0.96, opacity: 0 }, { y: 0, scale: 1, opacity: 1, duration: 0.5, ease: 'power3.out' });
      gsap.fromTo('.otp-inputs input', { y: 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4, stagger: 0.05, delay: 0.15, ease: 'power2.out' });
    }
    const t = setTimeout(() => inputsRef.current[0]?.focus(), 80);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (code: string) => {
    if (busy || verified) return;
    setBusy(true);
    setError('');
    setBad(false);
    try {
      await apiFetch('/auth/2fa/verify', { method: 'POST', body: { code } });
      setVerified(true);
      setTimeout(onVerified, 650);
    } catch (err) {
      setBusy(false);
      setBad(true);
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Too many attempts — wait a few minutes and try again.'
          : "That code didn't match. Check your device and try again.",
      );
      shake(cardRef.current);
      setDigits(Array(6).fill(''));
      inputsRef.current[0]?.focus();
    }
  };

  const setDigit = (idx: number, value: string) => {
    const v = value.replace(/\D/g, '').slice(0, 1);
    const next = [...digits];
    next[idx] = v;
    setDigits(next);
    setBad(false);
    setError('');
    if (v && idx < 5) inputsRef.current[idx + 1]?.focus();
    if (next.every((d) => d)) void verify(next.join(''));
  };

  const onKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      const next = [...digits];
      next[idx - 1] = '';
      setDigits(next);
      inputsRef.current[idx - 1]?.focus();
      e.preventDefault();
    }
    if (e.key === 'ArrowLeft' && idx > 0) inputsRef.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const onPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = Array(6)
      .fill('')
      .map((_, i) => pasted[i] ?? '');
    setDigits(next);
    inputsRef.current[Math.min(pasted.length, 5)]?.focus();
    if (pasted.length === 6) void verify(pasted);
  };

  const submitBackup = () => {
    const code = backupCode.trim().toUpperCase();
    if (!/^[A-Z2-7]{4}-?[A-Z2-7]{4}-?[A-Z2-7]{2}$/.test(code)) {
      setBad(true);
      setError('Backup codes look like XXXX-XXXX-XX.');
      shake(cardRef.current);
      return;
    }
    const dashed = code.includes('-') ? code : `${code.slice(0, 4)}-${code.slice(4, 8)}-${code.slice(8)}`;
    void verify(dashed);
  };

  return (
    <div
      className="scrim open"
      role="dialog"
      aria-modal="true"
      aria-labelledby="otpTitle"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="otp-card" ref={cardRef}>
        <div className="eyebrow">Two-Factor Verification</div>
        <h3 className="otp-title" id="otpTitle">
          {backupMode ? 'Use a backup code' : 'Enter your code'}
        </h3>
        <p className="otp-text">
          {backupMode
            ? 'Enter one of the single-use backup codes you saved when enrolling.'
            : 'Enter the 6-digit code from your authenticator app.'}
        </p>

        {backupMode ? (
          <div className="control otp-backup-input">
            <input
              type="text"
              placeholder="XXXX-XXXX-XX"
              autoComplete="one-time-code"
              autoFocus
              className={`${bad ? 'invalid' : ''}`}
              style={{ fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.15em', textAlign: 'center' }}
              value={backupCode}
              maxLength={12}
              onChange={(e) => {
                setBackupCode(e.target.value);
                setBad(false);
                setError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitBackup();
              }}
            />
          </div>
        ) : (
          <div className={`otp-inputs ${bad ? 'bad' : ''}`}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputsRef.current[i] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                aria-label={`Digit ${i + 1}`}
                className={d ? 'filled' : ''}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                onPaste={onPaste}
              />
            ))}
          </div>
        )}

        <p className={`otp-error ${error ? 'show' : ''}`} role="alert">
          {error}
        </p>

        <button
          className={`lg-btn otp-verify ${busy ? 'loading' : ''} ${verified ? 'success' : ''}`}
          type="button"
          disabled={busy || verified}
          onClick={() => (backupMode ? submitBackup() : void verify(digits.join('')))}
        >
          <span>{verified ? 'Verified' : busy ? 'Verifying…' : 'Verify code'}</span>
          <svg className="arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
          <span className="spinner" />
        </button>

        <p className="otp-foot">
          {backupMode ? (
            <>
              Have your authenticator?{' '}
              <button type="button" onClick={() => { setBackupMode(false); setError(''); setBad(false); }}>
                Enter a 6-digit code
              </button>
            </>
          ) : (
            <>
              Lost your device?{' '}
              <button type="button" onClick={() => { setBackupMode(true); setError(''); setBad(false); }}>
                Use a backup code
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
